import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Clock } from 'lucide-react';
import { WEEKDAYS, TIME_PERIODS, type TimePreferences } from '@/lib/time-preferences';

// US Time Zones in order of most common
const US_TIMEZONES = [
  'America/Chicago',    // Central
  'America/New_York',   // Eastern
  'America/Los_Angeles', // Pacific
  'America/Phoenix',    // Arizona
  'America/Denver',     // Mountain
  'America/Anchorage',  // Alaska
  'Pacific/Honolulu',   // Hawaii
];

export function TimePreferencesSettings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState<TimePreferences>({
    time_zone: 'America/Chicago', // Default to US Central
    preferred_days: [],
    preferred_times: [],
    notice_period: '1_day',
    virtual_preferred: true
  });

  useEffect(() => {
    if (user) {
      fetchPreferences();
    }
  }, [user]);

  const fetchPreferences = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('interview_preferences')
        .eq('id', user?.id)
        .single();

      if (error) throw error;
      if (data?.interview_preferences) {
        setPreferences(data.interview_preferences);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  const savePreferences = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          interview_preferences: preferences,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Time preferences updated successfully',
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to save preferences',
      });
    } finally {
      setLoading(false);
    }
  };

  // Get all time zones and sort with US zones first
  const getAllTimeZones = () => {
    const allZones = Intl.supportedValuesOf('timeZone');
    const otherZones = allZones.filter(zone => !US_TIMEZONES.includes(zone)).sort();
    return [...US_TIMEZONES, ...otherZones];
  };

  // Format time zone for display
  const formatTimeZone = (zone: string) => {
    try {
      const formatter = new Intl.DateTimeFormat('en', {
        timeZone: zone,
        timeZoneName: 'long',
        hour: 'numeric',
        minute: 'numeric',
      });
      const time = formatter.format(new Date());
      return `${zone.replace('_', ' ')} (${time})`;
    } catch (e) {
      return zone.replace('_', ' ');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary" />
          <CardTitle>Time & Availability Preferences</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Time Zone Selection */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Time Zone</label>
          <Select
            value={preferences.time_zone}
            onValueChange={(value) => setPreferences(prev => ({ ...prev, time_zone: value }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select time zone" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="America/Chicago">US Central (Chicago)</SelectItem>
              <SelectItem value="America/New_York">US Eastern (New York)</SelectItem>
              <SelectItem value="America/Los_Angeles">US Pacific (Los Angeles)</SelectItem>
              <SelectItem value="America/Phoenix">US Arizona (Phoenix)</SelectItem>
              <SelectItem value="America/Denver">US Mountain (Denver)</SelectItem>
              <SelectItem value="America/Anchorage">US Alaska (Anchorage)</SelectItem>
              <SelectItem value="Pacific/Honolulu">US Hawaii (Honolulu)</SelectItem>
              
              {/* Other time zones */}
              {getAllTimeZones()
                .filter(zone => !US_TIMEZONES.includes(zone))
                .map(zone => (
                  <SelectItem key={zone} value={zone}>
                    {formatTimeZone(zone)}
                  </SelectItem>
                ))
              }
            </SelectContent>
          </Select>
        </div>

        {/* Preferred Days */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Preferred Days</label>
          <div className="flex flex-wrap gap-2">
            {WEEKDAYS.map((day) => (
              <Button
                key={day}
                variant={preferences.preferred_days.includes(day) ? 'default' : 'outline'}
                onClick={() => {
                  const newDays = preferences.preferred_days.includes(day)
                    ? preferences.preferred_days.filter(d => d !== day)
                    : [...preferences.preferred_days, day];
                  setPreferences({ ...preferences, preferred_days: newDays });
                }}
                className="h-8"
              >
                {day}
              </Button>
            ))}
          </div>
        </div>
        
        {/* Preferred Times */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Preferred Times</label>
          <div className="flex flex-wrap gap-2">
            {TIME_PERIODS.map((period) => (
              <Button
                key={period.value}
                variant={preferences.preferred_times.includes(period.value) ? 'default' : 'outline'}
                onClick={() => {
                  const newTimes = preferences.preferred_times.includes(period.value)
                    ? preferences.preferred_times.filter(t => t !== period.value)
                    : [...preferences.preferred_times, period.value];
                  setPreferences({ ...preferences, preferred_times: newTimes });
                }}
                className="h-8"
              >
                {period.label}
              </Button>
            ))}
          </div>
        </div>
        
        {/* Notice Period */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Minimum Notice Period</label>
          <Select
            value={preferences.notice_period}
            onValueChange={(value) => setPreferences(prev => ({ ...prev, notice_period: value }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select notice period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1_day">1 Day</SelectItem>
              <SelectItem value="2_days">2 Days</SelectItem>
              <SelectItem value="3_days">3 Days</SelectItem>
              <SelectItem value="1_week">1 Week</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button onClick={savePreferences} disabled={loading}>
          {loading ? 'Saving...' : 'Save Preferences'}
        </Button>
      </CardContent>
    </Card>
  );
}